import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ProgressButtonDirective } from 'src/app/module/profet-control-widget/directive/progress-button.directive';
import { AccessControlDirective } from '../../directives/access-control.directive';
import { UserRole } from '../../interface/user.interface';

export interface ConfirmDialogConfig<T> {
  title: string;
  description?: string;
  actionMessage?: {
    message?: string;
    roles?: UserRole[];
  };
  primaryButtonLabel?: {
    label: string;
    roles?: UserRole[];
  };
  secondaryButtonLabel?: string;
  warnButtonLabel?: string;
  context?: T;
  primaryButtonRoles?: UserRole[];
  timer?: number;
}

export interface ConfirmDialogResult<T> {
  submitted: boolean;
  context?: T;
}

@Component({
  selector: 'app-confirm',
  standalone: true,
  imports: [MatButtonModule, ProgressButtonDirective, MatDialogModule, AccessControlDirective, MatIconModule],
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent<T = unknown> implements OnInit, OnDestroy {
  userRole = UserRole;

  public inProgress: boolean = false;

  public action: EventEmitter<ConfirmDialogResult<T>> = new EventEmitter<ConfirmDialogResult<T>>();
  public remainingSeconds: WritableSignal<number> = signal(0);
  private _timerId?: ReturnType<typeof setInterval>;

  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent, ConfirmDialogResult<T>>,
    @Inject(MAT_DIALOG_DATA) public dialogData: ConfirmDialogConfig<T>
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    if (this.dialogData.timer) this.setTimerForCloseDialog();
  }

  onAction(data: boolean) {
    this.inProgress = true;
    this.action.emit({ submitted: data, context: this.dialogData.context });
    this._clearTimer();
  }

  setTimerForCloseDialog() {
    this._timerId = setInterval(() => {
      this.remainingSeconds.update((value) => value - 1);

      if (this.remainingSeconds() <= 0) {
        this._clearTimer();

        this.action.emit({ submitted: true, context: this.dialogData.context });
      }
    }, 1000);

    this.remainingSeconds.set(this.dialogData.timer!);
  }

  ngOnDestroy(): void {
    this._clearTimer();
  }

  private _clearTimer(): void {
    if (!this._timerId) return;

    clearInterval(this._timerId);
    this._timerId = undefined;
  }

  get formattedDescription(): string {
    return this.dialogData.description?.replace(/<\s*timer\s*>/g, this.remainingSeconds()?.toString()) ?? '';
  }
}
